import { Controller } from "@hotwired/stimulus"

/**
 * ET Equipment Controller
 * Auto-populates ET report fields when equipment is selected/added
 *
 * Fields populated based on equipment type name:
 *   - If type contains "Instrument": Populates "ET Instrument" field
 *   - If type contains "Probe": Populates "Probe/Coil Type" and "Coil Diameter" fields
 *   - If type contains "Reference", "Block", or "Calibration":
 *       Populates "Reference Block S/N", "Reference Block Type", and "Notch Dimensions" fields
 *
 * Extraction patterns for auto-population:
 *   - Coil Diameter: Looks for patterns like "O5mm", "diameter: 5mm", "Crystal size: 5x5mm"
 *   - Reference Block Type: Detects from keywords (EDM, notch, flat bottom, step wedge, etc.)
 *   - Notch Dimensions: Extracts dimension patterns like "2.5mm" or "1x5mm"
 *
 * Listens for:
 *   - equipment:added - When equipment is added via equipment_controller
 *   - equipment:removed - When equipment is removed
 *
 * Usage in HAML:
 *   Attach this controller to a parent element containing both the equipment section
 *   and the method-specific-variables section.
 */
export default class extends Controller {
  static targets = [
    "equipmentSelect",    // Equipment dropdown selector (legacy)
    "instrumentField",    // ET Instrument text field (legacy)
    "probeTypeField",     // Probe/Coil Type text field (legacy)
    "coilDiameterField"   // Coil Diameter text field (legacy)
  ]

  connect() {
    console.log("[et-equipment] Controller connected")

    // Bind event handlers to preserve 'this' context
    this.handleEquipmentAddedBound = this.handleEquipmentAdded.bind(this)
    this.handleEquipmentRemovedBound = this.handleEquipmentRemoved.bind(this)

    // Listen for equipment add/remove events from equipment_controller
    document.addEventListener('equipment:added', this.handleEquipmentAddedBound)
    document.addEventListener('equipment:removed', this.handleEquipmentRemovedBound)

    console.log("[et-equipment] Event listeners registered for equipment:added and equipment:removed")
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('equipment:added', this.handleEquipmentAddedBound)
    document.removeEventListener('equipment:removed', this.handleEquipmentRemovedBound)

    console.log("[et-equipment] Event listeners removed")
  }

  /**
   * Handle equipment added event
   * Auto-populates ET fields based on equipment type
   */
  handleEquipmentAdded(event) {
    const { equipmentId, equipmentTypeName, equipmentData } = event.detail

    console.log("[et-equipment] Equipment added event received:", {
      equipmentId,
      equipmentTypeName,
      equipmentData
    })

    if (!equipmentTypeName) {
      console.log("[et-equipment] No equipment type name, skipping auto-populate")
      return
    }

    // Check if this is an Instrument type
    if (equipmentTypeName.toLowerCase().includes('instrument')) {
      console.log("[et-equipment] Detected Instrument type equipment")
      this.populateETInstrumentField(equipmentData)
    }

    // Check if this is a Probe type
    if (equipmentTypeName.toLowerCase().includes('probe')) {
      console.log("[et-equipment] Detected Probe type equipment")
      this.populateProbeTypeField(equipmentData)
    }

    // Check if this is a Reference Block type
    if (equipmentTypeName.toLowerCase().includes('reference') ||
        equipmentTypeName.toLowerCase().includes('block') ||
        equipmentTypeName.toLowerCase().includes('calibration')) {
      console.log("[et-equipment] Detected Reference Block type equipment")
      this.populateReferenceBlockFields(equipmentData)
    }
  }

  /**
   * Handle equipment removed event
   * Optionally clear fields when equipment is removed
   */
  handleEquipmentRemoved(event) {
    const { equipmentId, equipmentTypeName, equipmentData } = event.detail

    console.log("[et-equipment] Equipment removed event received:", {
      equipmentId,
      equipmentTypeName
    })

    // Optionally clear fields when equipment is removed
    // Currently we don't auto-clear as user might have edited the field
    // If desired, uncomment below:
    /*
    if (equipmentTypeName && equipmentTypeName.toLowerCase().includes('instrument')) {
      const instrumentField = this.findFieldByLabel('ET Instrument')
      if (instrumentField) {
        this.setFieldValue(instrumentField, '')
      }
    }

    if (equipmentTypeName && equipmentTypeName.toLowerCase().includes('probe')) {
      const probeField = this.findFieldByLabel('Probe/Coil Type')
      if (probeField) {
        this.setFieldValue(probeField, '')
      }
    }
    */
  }

  /**
   * Populate the ET Instrument field
   */
  populateETInstrumentField(equipmentData) {
    const instrumentField = this.findFieldByLabel('ET Instrument')

    if (!instrumentField) {
      console.log("[et-equipment] Could not find 'ET Instrument' field")
      return
    }

    const instrumentValue = equipmentData.report_text_or_name || equipmentData.name || ''
    this.setFieldValue(instrumentField, instrumentValue)
    console.log("[et-equipment] Set ET Instrument:", instrumentValue)
  }

  /**
   * Populate the Probe/Coil Type field and try to extract Coil Diameter
   */
  populateProbeTypeField(equipmentData) {
    const probeField = this.findFieldByLabel('Probe/Coil Type')

    if (probeField) {
      // For probe, use the equipment name (more descriptive than type)
      const probeValue = equipmentData.report_text_or_name || equipmentData.name || ''
      this.setFieldValue(probeField, probeValue)
      console.log("[et-equipment] Set Probe/Coil Type:", probeValue)
    } else {
      console.log("[et-equipment] Could not find 'Probe/Coil Type' field")
    }

    // Try to extract and set Coil Diameter
    const coilDiameterField = this.findFieldByLabel('Coil Diameter')
    if (coilDiameterField) {
      const diameter = this.extractCoilDiameter(equipmentData)
      if (diameter) {
        this.setFieldValue(coilDiameterField, diameter)
        console.log("[et-equipment] Set Coil Diameter:", diameter)
      }
    }
  }

  /**
   * Populate Reference Block fields (S/N, Type, Dimensions)
   */
  populateReferenceBlockFields(equipmentData) {
    // Reference Block S/N
    const serialField = this.findFieldByLabel('Reference Block S/N')
    if (serialField && equipmentData.serial_no) {
      this.setFieldValue(serialField, String(equipmentData.serial_no))
      console.log("[et-equipment] Set Reference Block S/N:", equipmentData.serial_no)
    }

    // Reference Block Type (dropdown)
    const typeField = this.findFieldByLabel('Reference Block Type')
    if (typeField) {
      const blockType = this.detectReferenceBlockType(equipmentData)
      if (blockType) {
        this.setFieldValue(typeField, blockType)
        console.log("[et-equipment] Set Reference Block Type:", blockType)
      }
    }

    // Notch Dimensions (if extractable)
    const dimensionField = this.findFieldByLabel('Notch Dimensions')
    if (dimensionField) {
      const dimensions = this.extractDimensions(equipmentData)
      if (dimensions) {
        this.setFieldValue(dimensionField, dimensions)
        console.log("[et-equipment] Set Notch Dimensions:", dimensions)
      }
    }
  }

  /**
   * Detect Reference Block Type from equipment data
   * Returns matching type string for dropdown selection
   */
  detectReferenceBlockType(equipmentData) {
    const searchText = `${equipmentData.name || ''} ${equipmentData.description || ''} ${equipmentData.model || ''}`.toLowerCase()

    if (searchText.includes('edm') || searchText.includes('notch')) {
      return 'EDM Notch Block'
    }
    if (searchText.includes('flat bottom') || searchText.includes('fbh') || searchText.includes('hole')) {
      return 'Flat Bottom Hole Block'
    }
    if (searchText.includes('step') || searchText.includes('wedge') || searchText.includes('ladder') || searchText.includes('trapp')) {
      return 'Step Wedge'
    }
    // Default to Custom for reference blocks
    return 'Custom'
  }

  /**
   * Extract dimensions from equipment description/model/name
   * Looks for patterns like "2.5mm" or "1x5mm"
   */
  extractDimensions(equipmentData) {
    const searchText = `${equipmentData.description || ''} ${equipmentData.model || ''} ${equipmentData.name || ''}`
    const match = searchText.match(/([\d.,]+(?:x[\d.,]+)?)\s*mm/i)
    return match ? match[1] + 'mm' : null
  }

  /**
   * Extract Coil Diameter from equipment description/model/name
   * Tries various patterns: diameter symbol, text patterns, crystal size
   */
  extractCoilDiameter(equipmentData) {
    const searchText = `${equipmentData.description || ''} ${equipmentData.model || ''} ${equipmentData.name || ''}`

    // Try diameter symbol pattern: O5mm or O 5mm or Ø5mm
    let match = searchText.match(/[OØ]\s*([\d.,]+)\s*mm/i)
    if (match) return match[1] + 'mm'

    // Try text "diameter" pattern
    match = searchText.match(/diameter[:\s]*([\d.,]+)\s*mm/i)
    if (match) return match[1] + 'mm'

    // Try "Crystal size" pattern (may include dimensions like 5x5)
    match = searchText.match(/Crystal size[:\s]*([\d.,]+(?:x[\d.,]+)?)\s*mm/i)
    if (match) return match[1] + 'mm'

    // Try "coil" pattern
    match = searchText.match(/coil[:\s]*([\d.,]+)\s*mm/i)
    if (match) return match[1] + 'mm'

    // Try generic "size" pattern
    match = searchText.match(/size[:\s]*([\d.,]+(?:x[\d.,]+)?)\s*mm/i)
    if (match) return match[1] + 'mm'

    // Try any mm pattern as last resort
    match = searchText.match(/([\d.,]+)\s*mm/i)
    if (match) return match[1] + 'mm'

    return null
  }

  /**
   * Find a form field by its label text
   * Searches within the controller's element for a label with matching text
   * and returns the associated input field
   */
  findFieldByLabel(labelText) {
    const labels = this.element.querySelectorAll('label')

    for (const label of labels) {
      if (label.textContent.trim() === labelText) {
        // Find the parent form group container
        const formGroup = label.closest('.form-group, .col-sm-4, .col-md-6')

        if (formGroup) {
          // Look for input field with method_specific class
          const input = formGroup.querySelector('input.method_specific, select.method_specific')
          if (input) {
            console.log("[et-equipment] Found field for label:", labelText, "Field ID:", input.id)
            return input
          }
        }
      }
    }

    console.log("[et-equipment] Could not find field for label:", labelText)
    return null
  }

  /**
   * Handle equipment selection change (legacy - for direct select dropdown usage)
   * Fetches equipment data and auto-populates related fields
   */
  async handleEquipmentChange(event) {
    const equipmentId = event.target.value

    if (!equipmentId) {
      console.log("[et-equipment] No equipment selected, skipping auto-populate")
      return
    }

    console.log("[et-equipment] Equipment selected:", equipmentId)

    try {
      const response = await fetch(`/equipment/${equipmentId}.json`)

      if (!response.ok) {
        throw new Error(`Failed to fetch equipment data: ${response.status}`)
      }

      const equipmentData = await response.json()
      console.log("[et-equipment] Equipment data received:", equipmentData)

      this.populateFields(equipmentData)
    } catch (error) {
      console.error("[et-equipment] Failed to fetch equipment data:", error)
    }
  }

  /**
   * Populate ET fields with equipment data (legacy method for target-based fields)
   */
  populateFields(equipmentData) {
    // ET Instrument - use report_text_or_name (falls back to name in model)
    if (this.hasInstrumentFieldTarget) {
      const instrumentValue = equipmentData.report_text_or_name || equipmentData.name || ''
      this.setFieldValue(this.instrumentFieldTarget, instrumentValue)
      console.log("[et-equipment] Set ET Instrument:", instrumentValue)
    }

    // Probe/Coil Type - use equipment_type_name or model
    if (this.hasProbeTypeFieldTarget) {
      const probeTypeValue = equipmentData.equipment_type_name || equipmentData.model || ''
      this.setFieldValue(this.probeTypeFieldTarget, probeTypeValue)
      console.log("[et-equipment] Set Probe/Coil Type:", probeTypeValue)
    }

    // Coil Diameter - currently not available in equipment data
    // This field is left for manual entry or future equipment metadata
    if (this.hasCoilDiameterFieldTarget) {
      // Reserved for future implementation when coil diameter is added to equipment
      // For now, we don't auto-populate this field
      console.log("[et-equipment] Coil Diameter field present but no data to populate")
    }
  }

  /**
   * Helper to get field value, handling both regular inputs and Tom Select dropdowns
   */
  getFieldValue(input) {
    if (!input) return ''

    if (input.tomselect) {
      return input.tomselect.getValue()
    }
    return input.value
  }

  /**
   * Helper to set field value, handling both regular inputs and Tom Select dropdowns
   * Also triggers change event for autosave compatibility
   */
  setFieldValue(input, value) {
    if (!input) return

    if (input.tomselect) {
      input.tomselect.setValue(value)
    } else {
      input.value = value
    }

    // Trigger change event for autosave and other listeners
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
